import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  RefreshCw,
  Plus,
  Mail,
  Trash2,
  Edit,
  Play,
  Shield,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_RULE = {
  name: '',
  description: '',
  priority: 100,
  enabled: true,
  stop_on_match: true,
  conditions: {
    from_regex: '',
    to_regex: '',
    subject_contains: '',
    body_contains: '',
    has_attachment: null,
  },
  actions: {
    route_to_workspace_id: '',
    route_to_contact_id: '',
    drop_as_spam: false,
    forward_to_email: '',
    tag: '',
  },
};

const SAMPLE_PAYLOAD = {
  from_email: 'someone@example.com',
  to: ['support@countbucks.com'],
  subject: 'Sample subject',
  text: 'Sample body text',
  html: '',
  attachments: [],
};

function ConditionsForm({ value, onChange }) {
  const setField = (key, v) => onChange({ ...value, [key]: v });
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">From regex</Label>
        <Input
          placeholder=".*@spam\\.com$"
          value={value.from_regex || ''}
          onChange={(e) => setField('from_regex', e.target.value)}
          data-testid="rule-cond-from-regex"
        />
      </div>
      <div>
        <Label className="text-xs">To regex (matches any To address)</Label>
        <Input
          placeholder="^billing@"
          value={value.to_regex || ''}
          onChange={(e) => setField('to_regex', e.target.value)}
          data-testid="rule-cond-to-regex"
        />
      </div>
      <div>
        <Label className="text-xs">Subject contains</Label>
        <Input
          placeholder="payment failed"
          value={value.subject_contains || ''}
          onChange={(e) => setField('subject_contains', e.target.value)}
          data-testid="rule-cond-subject"
        />
      </div>
      <div>
        <Label className="text-xs">Body contains</Label>
        <Input
          placeholder="invoice"
          value={value.body_contains || ''}
          onChange={(e) => setField('body_contains', e.target.value)}
          data-testid="rule-cond-body"
        />
      </div>
      <div>
        <Label className="text-xs">Attachments</Label>
        <Select
          value={value.has_attachment === null || value.has_attachment === undefined ? 'any' : (value.has_attachment ? 'yes' : 'no')}
          onValueChange={(v) => setField('has_attachment', v === 'any' ? null : v === 'yes')}
        >
          <SelectTrigger data-testid="rule-cond-attachments">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any (no filter)</SelectItem>
            <SelectItem value="yes">Must have attachments</SelectItem>
            <SelectItem value="no">Must NOT have attachments</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function ActionsForm({ value, onChange }) {
  const setField = (key, v) => onChange({ ...value, [key]: v });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2">
        <div>
          <Label className="text-xs text-rose-300">Drop as spam</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">Email is discarded without being stored.</p>
        </div>
        <Switch
          checked={!!value.drop_as_spam}
          onCheckedChange={(v) => setField('drop_as_spam', v)}
          data-testid="rule-action-drop"
        />
      </div>
      <div>
        <Label className="text-xs">Route to workspace ID</Label>
        <Input
          placeholder="ws_xxxxxxxxxxxx"
          value={value.route_to_workspace_id || ''}
          onChange={(e) => setField('route_to_workspace_id', e.target.value)}
          disabled={!!value.drop_as_spam}
          data-testid="rule-action-workspace"
        />
      </div>
      <div>
        <Label className="text-xs">Route to contact ID</Label>
        <Input
          placeholder="contact_xxxxxxxxxxxx"
          value={value.route_to_contact_id || ''}
          onChange={(e) => setField('route_to_contact_id', e.target.value)}
          disabled={!!value.drop_as_spam}
          data-testid="rule-action-contact"
        />
      </div>
      <div>
        <Label className="text-xs">Forward to email</Label>
        <Input
          type="email"
          placeholder="ops@countbucks.com"
          value={value.forward_to_email || ''}
          onChange={(e) => setField('forward_to_email', e.target.value)}
          disabled={!!value.drop_as_spam}
          data-testid="rule-action-forward"
        />
      </div>
      <div>
        <Label className="text-xs">Tag (free-form label)</Label>
        <Input
          placeholder="vip-customer"
          value={value.tag || ''}
          onChange={(e) => setField('tag', e.target.value)}
          data-testid="rule-action-tag"
        />
      </div>
    </div>
  );
}

function RuleEditorDialog({ open, onOpenChange, initial, onSave, submitting }) {
  const [rule, setRule] = useState(initial || EMPTY_RULE);
  const [testPayload, setTestPayload] = useState(JSON.stringify(SAMPLE_PAYLOAD, null, 2));
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setRule(initial || EMPTY_RULE);
    setTestResult(null);
  }, [initial, open]);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      let parsedPayload;
      try {
        parsedPayload = JSON.parse(testPayload);
      } catch {
        toast.error('Invalid JSON in test payload');
        setTesting(false);
        return;
      }
      const r = await fetch(`${API_URL}/api/owner-admin/email-rules/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rule, payload: parsedPayload }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Test failed');
      } else {
        setTestResult(data);
      }
    } catch (e) {
      toast.error('Test request failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-rule-dialog">
        <DialogHeader>
          <DialogTitle>{rule.rule_id ? 'Edit rule' : 'Create email routing rule'}</DialogTitle>
          <DialogDescription>
            Rules run in priority order against every inbound email (lower = earlier). First matching rule wins by default.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={rule.name}
                onChange={(e) => setRule({ ...rule, name: e.target.value })}
                placeholder="Auto-route Acme replies"
                data-testid="rule-name"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={rule.description || ''}
                onChange={(e) => setRule({ ...rule, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Input
                  type="number"
                  value={rule.priority}
                  onChange={(e) => setRule({ ...rule, priority: parseInt(e.target.value || '100', 10) })}
                  data-testid="rule-priority"
                />
              </div>
              <div className="flex items-end gap-3">
                <div className="flex flex-col">
                  <Label className="mb-2">Enabled</Label>
                  <Switch
                    checked={rule.enabled}
                    onCheckedChange={(v) => setRule({ ...rule, enabled: v })}
                    data-testid="rule-enabled"
                  />
                </div>
                <div className="flex flex-col">
                  <Label className="mb-2">Stop on match</Label>
                  <Switch
                    checked={rule.stop_on_match}
                    onCheckedChange={(v) => setRule({ ...rule, stop_on_match: v })}
                  />
                </div>
              </div>
            </div>

            <div className="pt-2">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Conditions (all must match)
              </h4>
              <ConditionsForm
                value={rule.conditions}
                onChange={(c) => setRule({ ...rule, conditions: c })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Actions
              </h4>
              <ActionsForm
                value={rule.actions}
                onChange={(a) => setRule({ ...rule, actions: a })}
              />
            </div>

            <div className="pt-2 border-t border-border">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Play className="w-4 h-4" /> Test this rule
              </h4>
              <Textarea
                value={testPayload}
                onChange={(e) => setTestPayload(e.target.value)}
                rows={6}
                className="font-mono text-xs"
                data-testid="rule-test-payload"
              />
              <Button
                type="button"
                onClick={runTest}
                disabled={testing}
                size="sm"
                className="mt-2"
                variant="secondary"
                data-testid="rule-test-run"
              >
                {testing ? 'Testing…' : 'Run test'}
              </Button>
              {testResult && (
                <div className="mt-2 border border-border rounded-md p-3 text-xs" data-testid="rule-test-result">
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.matched ? (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Matched
                      </Badge>
                    ) : (
                      <Badge className="bg-zinc-500/20 text-zinc-300 border-zinc-500/30">
                        <XCircle className="w-3 h-3 mr-1" /> No match
                      </Badge>
                    )}
                    {testResult.would_drop && (
                      <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/30">Would drop</Badge>
                    )}
                  </div>
                  {testResult.matched && (
                    <pre className="text-[11px] whitespace-pre-wrap font-mono">
                      {JSON.stringify(testResult.preview_overrides, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => onSave(rule)} disabled={submitting || !rule.name.trim()} data-testid="rule-save">
            {rule.rule_id ? 'Save changes' : 'Create rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OwnerAdminEmailRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, rule: null });
  const [submitting, setSubmitting] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/owner-admin/email-rules`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setRules(data.rules || []);
      } else {
        toast.error('Failed to load rules');
      }
    } catch {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleSave = async (rule) => {
    setSubmitting(true);
    try {
      const url = rule.rule_id
        ? `${API_URL}/api/owner-admin/email-rules/${rule.rule_id}`
        : `${API_URL}/api/owner-admin/email-rules`;
      const method = rule.rule_id ? 'PUT' : 'POST';

      // Strip rule_id from body (server doesn't expect it).
      const { rule_id, match_count, last_matched_at, created_at, updated_at, ...payload } = rule;

      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Failed to save rule');
        return;
      }
      toast.success(rule.rule_id ? 'Rule updated' : 'Rule created');
      setEditor({ open: false, rule: null });
      fetchRules();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/owner-admin/email-rules/${rule.rule_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) {
        toast.error('Failed to delete');
        return;
      }
      toast.success('Rule deleted');
      fetchRules();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleReorder = async (from, to) => {
    if (to < 0 || to >= rules.length) return;
    const newOrder = [...rules];
    const [moved] = newOrder.splice(from, 1);
    newOrder.splice(to, 0, moved);
    setRules(newOrder);
    try {
      await fetch(`${API_URL}/api/owner-admin/email-rules/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ order: newOrder.map(r => r.rule_id) }),
      });
      fetchRules();
    } catch {
      toast.error('Failed to reorder');
      fetchRules();
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="owner-admin-email-rules">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-emerald-400" />
            Email Routing Rules
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customize how inbound Resend emails are routed, tagged, dropped, or forwarded.
            Rules run BEFORE the default In-Reply-To threading.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchRules} data-testid="refresh-rules">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setEditor({ open: true, rule: null })} data-testid="create-rule">
            <Plus className="w-4 h-4 mr-2" />
            New rule
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All rules ({rules.length})</CardTitle>
          <CardDescription>
            Lower priority numbers run first. Disabled rules are skipped entirely.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No rules yet. Click "New rule" to create your first one.
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule, idx) => (
                <div
                  key={rule.rule_id}
                  className="flex items-center gap-3 border border-border rounded-lg p-3 hover:bg-muted/30 transition-colors"
                  data-testid={`rule-row-${rule.rule_id}`}
                >
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => handleReorder(idx, idx - 1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleReorder(idx, idx + 1)} disabled={idx === rules.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="w-12 text-center">
                    <span className="text-xs font-mono text-muted-foreground">#{rule.priority}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{rule.name}</span>
                      {!rule.enabled && (
                        <Badge variant="outline" className="text-[10px] border-zinc-500/40 text-zinc-400">Disabled</Badge>
                      )}
                      {rule.actions?.drop_as_spam && (
                        <Badge className="text-[10px] bg-rose-500/20 text-rose-300 border-rose-500/30">Drop</Badge>
                      )}
                      {rule.actions?.tag && (
                        <Badge className="text-[10px] bg-blue-500/20 text-blue-300 border-blue-500/30">Tag: {rule.actions.tag}</Badge>
                      )}
                      {rule.match_count > 0 && (
                        <Badge variant="outline" className="text-[10px]">{rule.match_count} matches</Badge>
                      )}
                    </div>
                    {rule.description && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">{rule.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditor({ open: true, rule })} data-testid={`edit-rule-${rule.rule_id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(rule)} className="text-rose-400 hover:text-rose-300" data-testid={`delete-rule-${rule.rule_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <RuleEditorDialog
        open={editor.open}
        onOpenChange={(o) => setEditor({ open: o, rule: o ? editor.rule : null })}
        initial={editor.rule}
        onSave={handleSave}
        submitting={submitting}
      />
    </div>
  );
}
